import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Link,
  Tooltip,
} from "@mui/material";
import { useNotification } from "@platform/hooks";
import { Typography } from "@platform/design-system";

import { useReprocessDocument } from "../hooks/useDocumentsApi";
import { useDownloadDocument } from "../hooks";
import { resolveProcessingError } from "../errorMessages";
import { formatDate } from "../utils";

import type {
  DocumentStatus,
  ProposalDocument as ProposalDocumentType,
} from "../types";

const STATUS_COLOR: Record<
  DocumentStatus,
  "error" | "success" | "info" | "warning"
> = {
  Error: "error",
  Extracted: "success",
  Processing: "info",
};
interface ProposalDocumentProps {
  doc: ProposalDocumentType;
  ecoRfpId: string;
}

export function ProposalDocument({
  doc,
  ecoRfpId,
}: ProposalDocumentProps): React.JSX.Element {
  const { downloadDocument } = useDownloadDocument();
  const { showError } = useNotification();
  const { reprocess, reprocessingId, isReprocessing } =
    useReprocessDocument(ecoRfpId);

  const handleClick = (): void => {
    if (!doc.downloadUrl) return;
    void downloadDocument(doc.downloadUrl, doc.name).catch(() => {
      showError("Failed to download document. Please try again later.");
    });
  };

  const processingError =
    doc.status === "Error" ? resolveProcessingError(doc.errorCode) : null;
  const isReprocessingThisDoc = reprocessingId === doc.id;

  return (
    <Box
      data-testid="proposal-document"
      sx={{
        py: 1,
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "space-between",
      }}
    >
      <Tooltip title={doc.name} placement="top-start">
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Link
            component="button"
            type="button"
            onClick={handleClick}
            underline="always"
            disabled={!doc.downloadUrl}
            sx={{
              display: "block",
              textAlign: "left",
              cursor: doc.downloadUrl ? "pointer" : "default",
              color: doc.downloadUrl ? "primary.secondary" : "text.disabled",
              "&:disabled": {
                pointerEvents: "none",
              },
            }}
          >
            <Typography
              className="doc-name"
              variant="body-medium"
              noWrap
              color="inherit"
            >
              {doc.name}
            </Typography>
          </Link>
          <Typography variant="caption" color="textSecondary" display="block">
            {`created ${formatDate(doc.createdAt)}`}
          </Typography>
          {processingError && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                flexWrap: "wrap",
              }}
            >
              <Typography
                data-testid="proposal-document-error"
                variant="caption"
                color="danger"
                display="block"
              >
                {processingError.message}
              </Typography>
              {processingError.retriable && (
                // TODO(ds-button): no DS low-emphasis danger variant yet
                <Button
                  data-testid="proposal-document-rerun"
                  variant="text"
                  size="small"
                  color="error"
                  disabled={isReprocessing}
                  startIcon={
                    isReprocessingThisDoc ? (
                      <CircularProgress size={12} color="inherit" />
                    ) : null
                  }
                  onClick={() => reprocess(doc.id)}
                  sx={{ minWidth: 0, p: 0 }}
                >
                  {isReprocessingThisDoc ? "Re-running..." : "Re-run"}
                </Button>
              )}
            </Box>
          )}
        </Box>
      </Tooltip>

      <Box display="flex" alignItems="center" gap={1} sx={{ flexShrink: 0 }}>
        <Chip
          label={doc.status}
          variant="outlined"
          color={STATUS_COLOR[doc.status]}
          sx={{ borderRadius: 1 }}
        />
      </Box>
    </Box>
  );
}
